import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { X } from 'lucide-react';
import { legacyApi, LegacySupplier } from '../../../services/legacyApi';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { RequiredLabel } from '../../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField } from '../../../components/forms/formValidationUtils';

const schema = z.object({
    supplier_name: z.string().min(1, 'Name is required'),
    poc_name: z.string().optional(),
    poc_contact: z.string().optional(),
    emirate: z.string().optional(),
    office_tel: z.string().optional(),
    email_address: z.string().optional(),
    trn: z.string().optional(),
    address: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    initialData?: LegacySupplier | null;
}

export function SupplierForm({ isOpen, onClose, onSuccess, initialData }: Props) {
    const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            supplier_name: '',
            poc_name: '',
            poc_contact: '',
            emirate: '',
            office_tel: '',
            email_address: '',
            trn: '',
            address: ''
        }
    });

    useEffect(() => {
        if (initialData) {
            reset({
                supplier_name: initialData.supplier_name,
                poc_name: initialData.poc_name || '',
                poc_contact: initialData.poc_contact || '',
                emirate: initialData.emirate || '',
                office_tel: initialData.office_tel || '',
                email_address: initialData.email_address || '',
                trn: initialData.trn || '',
                address: initialData.address || ''
            });
        } else {
            reset({
                supplier_name: '',
                poc_name: '',
                poc_contact: '',
                emirate: '',
                office_tel: '',
                email_address: '',
                trn: '',
                address: ''
            });
        }
    }, [initialData, reset]);

    const onSubmit = async (data: FormData) => {
        try {
            if (initialData) {
                await legacyApi.suppliers.update(initialData.supplier_id, data);
            } else {
                await legacyApi.suppliers.create(data);
            }
            onSuccess();
        } catch (e) {
            toast.error("Failed to save supplier");
        }
    };
    const onInvalid = (formErrors: Record<string, any>) => {
        showValidationSummaryToast();
        focusFirstInvalidField(Object.keys(formErrors)[0]);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
                <div className="flex justify-between items-center p-4 border-b bg-slate-50">
                    <h2 className="text-lg font-semibold">{initialData ? 'Edit Supplier' : 'Add Supplier'}</h2>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-700">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="p-6 space-y-4 overflow-y-auto max-h-[75vh]">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2 space-y-1">
                            <RequiredLabel required htmlFor="supplier_name">Supplier Name</RequiredLabel>
                            <input
                                id="supplier_name"
                                {...register('supplier_name')}
                                aria-invalid={!!errors.supplier_name}
                                aria-required="true"
                                aria-describedby={errors.supplier_name ? 'supplier_name_error' : undefined}
                                className="w-full rounded border border-slate-300 p-2 text-sm aria-[invalid=true]:border-red-500"
                                placeholder="Company Name"
                            />
                            <FieldErrorText id="supplier_name_error" message={errors.supplier_name?.message} />
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium">POC Name</label>
                            <input
                                {...register('poc_name')}
                                className="w-full rounded border border-slate-300 p-2 text-sm"
                                placeholder="Contact Person"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium">POC Contact</label>
                            <input
                                {...register('poc_contact')}
                                className="w-full rounded border border-slate-300 p-2 text-sm"
                                placeholder="Mobile/Phone"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium">Email Address</label>
                            <input
                                {...register('email_address')}
                                className="w-full rounded border border-slate-300 p-2 text-sm"
                                placeholder="email@example.com"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium">Office Tel</label>
                            <input
                                {...register('office_tel')}
                                className="w-full rounded border border-slate-300 p-2 text-sm"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium">Emirate</label>
                            <input
                                {...register('emirate')}
                                className="w-full rounded border border-slate-300 p-2 text-sm"
                                placeholder="Dubai, Abu Dhabi..."
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium">TRN</label>
                            <input
                                {...register('trn')}
                                className="w-full rounded border border-slate-300 p-2 text-sm"
                            />
                        </div>

                        <div className="col-span-2 space-y-1">
                            <label className="text-sm font-medium">Address</label>
                            <textarea
                                {...register('address')}
                                className="w-full rounded border border-slate-300 p-2 text-sm"
                                rows={3}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                            {isSubmitting ? 'Saving...' : 'Save Supplier'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
